import React from 'react';
import { cn } from '@/lib/utils';

export const Container = ({ children, className, size = 'md', padding = true, centered = true, ...props }) => {
  const sizeStyles = { sm: 'max-w-xl', md: 'max-w-3xl', lg: 'max-w-5xl', xl: 'max-w-6xl', full: 'max-w-full' };
  return (
    <div className={cn('w-full', sizeStyles[size], centered && 'mx-auto', padding && 'px-4 sm:px-6 lg:px-8', className)} {...props}>
      {children}
    </div>
  );
};
Container.displayName = 'Container';

export const Section = ({ children, className, spacing = 'md', as: Tag = 'section', ...props }) => {
  const spacingStyles = { sm: 'py-8', md: 'py-12', lg: 'py-16', xl: 'py-24' };
  return <Tag className={cn(spacingStyles[spacing], className)} {...props}>{children}</Tag>;
};
Section.displayName = 'Section';

export const Spacer = ({ className, size = 'md', axis = 'vertical', ...props }) => {
  const sizeStyles = {
    vertical: { xs: 'h-2', sm: 'h-3', md: 'h-5', lg: 'h-8', xl: 'h-12', '2xl': 'h-16' },
    horizontal: { xs: 'w-2', sm: 'w-3', md: 'w-5', lg: 'w-8', xl: 'w-12', '2xl': 'w-16' },
  };
  return (
    <div
      className={cn(axis === 'vertical' ? sizeStyles.vertical[size] : sizeStyles.horizontal[size], axis === 'horizontal' && 'inline-block', className)}
      aria-hidden="true"
      {...props}
    />
  );
};
Spacer.displayName = 'Spacer';

export const Stack = ({ children, className, spacing = 'md', align = 'stretch', ...props }) => {
  const spacingStyles = { xs: 'gap-2', sm: 'gap-3', md: 'gap-4', lg: 'gap-6', xl: 'gap-8' };
  const alignStyles = { start: 'items-start', center: 'items-center', end: 'items-end', stretch: 'items-stretch' };
  return (
    <div className={cn('flex flex-col', spacingStyles[spacing], alignStyles[align], className)} {...props}>
      {children}
    </div>
  );
};
Stack.displayName = 'Stack';

export const Row = ({ children, className, spacing = 'md', align = 'center', justify = 'start', wrap = false, ...props }) => {
  const spacingStyles = { xs: 'gap-2', sm: 'gap-3', md: 'gap-4', lg: 'gap-6', xl: 'gap-8' };
  const alignStyles = { start: 'items-start', center: 'items-center', end: 'items-end', baseline: 'items-baseline', stretch: 'items-stretch' };
  const justifyStyles = { start: 'justify-start', center: 'justify-center', end: 'justify-end', between: 'justify-between', around: 'justify-around' };
  return (
    <div className={cn('flex', spacingStyles[spacing], alignStyles[align], justifyStyles[justify], wrap && 'flex-wrap', className)} {...props}>
      {children}
    </div>
  );
};
Row.displayName = 'Row';

export const Center = ({ children, className, ...props }) => {
  return <div className={cn('flex items-center justify-center', className)} {...props}>{children}</div>;
};
Center.displayName = 'Center';

export const Divider = ({ className, orientation = 'horizontal', variant = 'subtle', spacing = 'md', ...props }) => {
  const variantStyles = { subtle: 'bg-[#FFBB98]', medium: 'bg-[#7D8E95]', strong: 'bg-[#B0A89E]' };
  const spacingHorizontal = { sm: 'my-3', md: 'my-6', lg: 'my-8' };
  const spacingVertical = { sm: 'mx-3', md: 'mx-6', lg: 'mx-8' };
  if (orientation === 'vertical') {
    return <div className={cn('w-px self-stretch', variantStyles[variant], spacingVertical[spacing], className)} role="separator" aria-orientation="vertical" {...props} />;
  }
  return <hr className={cn('border-none h-px w-full', variantStyles[variant], spacingHorizontal[spacing], className)} role="separator" {...props} />;
};
Divider.displayName = 'Divider';

export const Grid = ({ children, className, cols = 2, gap = 'md', ...props }) => {
  const gapStyles = { sm: 'gap-3', md: 'gap-4', lg: 'gap-6', xl: 'gap-8' };
  const colsMap = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5', 6: 'grid-cols-6' };
  const responsiveColsMap = {
    sm: { 1: 'sm:grid-cols-1', 2: 'sm:grid-cols-2', 3: 'sm:grid-cols-3', 4: 'sm:grid-cols-4' },
    md: { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4' },
    lg: { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4' },
    xl: { 1: 'xl:grid-cols-1', 2: 'xl:grid-cols-2', 3: 'xl:grid-cols-3', 4: 'xl:grid-cols-4' },
  };
  const getColsClass = () => {
    if (typeof cols === 'number') return colsMap[cols] || 'grid-cols-2';
    const classes = [];
    if (cols.sm) classes.push(responsiveColsMap.sm[cols.sm] || '');
    if (cols.md) classes.push(responsiveColsMap.md[cols.md] || '');
    if (cols.lg) classes.push(responsiveColsMap.lg[cols.lg] || '');
    if (cols.xl) classes.push(responsiveColsMap.xl[cols.xl] || '');
    return classes.filter(Boolean).join(' ');
  };
  return (
    <div className={cn('grid grid-cols-1', getColsClass(), gapStyles[gap], className)} {...props}>
      {children}
    </div>
  );
};
Grid.displayName = 'Grid';

export const PageLayout = ({ children, className, header, footer, ...props }) => {
  return (
    <div className={cn('min-h-screen flex flex-col', className)} {...props}>
      {header}
      <main className="flex-1">{children}</main>
      {footer}
    </div>
  );
};
PageLayout.displayName = 'PageLayout';

export default Container;
